import styled, { css } from 'styled-components';

import { type Color, Colors, type Variant, Variants } from './types';

export const BUTTON_BASE_RESET_CSS = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    background-color: transparent;
    outline: 0;
    border: 0;
    margin: 0;
    border-radius: 0;
    padding: 0;
    user-select: none;
    vertical-align: middle;
    appearance: none;
    text-decoration: none;
    text-align: center;
    :not(:disabled) {
        cursor: pointer;
    }
`;

type StyledButtonBaseProps = {
    $color?: Color;
    $variant?: Variant;
};
export const StyledButtonBase = styled.button<StyledButtonBaseProps>`
    && {
        ${BUTTON_BASE_RESET_CSS}

        color: ${(props) => props.theme.colors.white};

        ${({ theme, $color, $variant }) => {
            switch ($variant) {
                case Variants.solid: {
                    switch ($color) {
                        case Colors.primary:
                            return css`
                                background: ${theme.colors.purple};
                            `;
                        case Colors.secondary:
                            return css`
                                background: ${theme.shades.white[10]};
                            `;
                        case Colors.success:
                            return css`
                                background: ${theme.colors.greenDark};
                            `;
                        case Colors.translucent:
                            return css`
                                background: ${theme.shades.white[10]};
                                backdrop-filter: blur(54px);
                            `;
                        case Colors.transparent:
                            return css`
                                background: transparent;
                                transition: background 0.25s ease;

                                :focus,
                                :hover {
                                    background: ${theme.shades.white[5]};
                                }
                            `;
                        case Colors.warning:
                            return css`
                                background: ${theme.colors.red};
                                transition: background 0.25s ease;

                                :focus,
                                :hover {
                                    background: #ff418e;
                                }
                                :disabled {
                                    background: #212031;
                                    color: ${theme.shades.white[50]};
                                }
                            `;
                        case Colors.error:
                            return css`
                                background: ${theme.shades.red[10]};
                                border: 1px solid red;
                            `;
                        case Colors.white:
                            return css`
                                background: ${theme.colors.white};
                                color: #0a0a0a;

                                :disabled {
                                    color: ${({ theme }) => theme.shades.black['55']};
                                }
                            `;
                        case Colors.gradient:
                            return css`
                                background: linear-gradient(180deg, #3f3b58 0%, #221a25 100%);
                            `;
                    }
                    break;
                }

                case Variants.yat: {
                    switch ($color) {
                        case Colors.primary: {
                            return css`
                                box-shadow:
                                    0 0 15px ${theme.shades.purple[5]},
                                    inset 0 0 20px ${theme.colors.purple};

                                @media (min-width: ${theme.breakpoints.mobile}) {
                                    box-shadow:
                                        0 0 15px ${theme.shades.purple[5]},
                                        inset 0 0 40px ${theme.colors.purple};
                                }
                            `;
                        }
                        case Colors.success: {
                            return css`
                                box-shadow:
                                    0 0 16px ${theme.shades.green[60]},
                                    inset 0 0 32px ${theme.shades.green[100]};

                                @media (min-width: ${theme.breakpoints.mobile}) {
                                    box-shadow:
                                        0 0 20px ${theme.shades.green[60]},
                                        inset 0 0 36px ${theme.shades.green[100]};
                                }
                            `;
                        }
                        case Colors.error:
                            return css`
                                box-shadow:
                                    0 0 15px ${theme.shades.red[60]},
                                    inset 0 0 20px ${theme.colors.red};

                                @media (min-width: ${theme.breakpoints.mobile}) {
                                    box-shadow:
                                        0 0 30px ${theme.shades.red[60]},
                                        inset 0 0 54px ${theme.colors.red};
                                }
                            `;

                        case Colors.white:
                            return css`
                                background: ${theme.colors.white};
                                color: #0a0a0a;
                            `;
                    }
                    break;
                }

                case Variants.foggy:
                    return css`
                        background: #222030;

                        @supports (backdrop-filter: blur(1px)) {
                            background: ${({ theme }) => theme.shades.white['10']};
                            backdrop-filter: blur(40px);
                        }

                        @-moz-document url-prefix() {
                            background: #222030; // Force color on FireFox, backdrop-filter isnt working properly
                        }
                    `;

                case Variants.highlight:
                    return css`
                        background: linear-gradient(300.86deg, #523df1 39.32%, #976af6 92.76%);
                    `;
            }
        }}

        ::-moz-focus-inner {
            border-style: none;
        }

        :disabled {
            opacity: 0.4;
        }
    }
`;
